import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useEngagement, useNavigation } from "../stores/useAppStore";
import type {
    DockButtonConfig,
    DockFlyoutConfig,
    DockFlyoutGroup,
    DockFlyoutHeaderAction,
    DockFlyoutKind,
    DockFlyoutDataModel,
    DockFlyoutFilterSection,
    DockFlyoutFiltersState,
    DockFlyoutListItem,
    LoadDockFlyoutDataParams,
    DockFlyoutRowAction,
} from "kci-design-lib";
import {
    buildObjectiveSecondaryGroup,
    buildSyncedObjectiveSecondaryGroup,
    fetchWorkspaceObjectives,
    loadDockFlyoutData,
    syncWorkspace,
} from "../services/dockFlyoutDataService";
import {
    bulkDeleteChatSessions,
    deleteAllChatSessions,
    deleteChatSession,
    downloadAllChats,
    downloadAllUploadedFiles,
    downloadChat,
    downloadSelectedChats,
    getChatSession,
    renameChatSession,
    updateChatSessionPin,
} from "../services/chatHistoryApi";
import { CONFIG, CHAT_HISTORY_CONFIG } from "../constants";

interface DockFeatureState {
    data: DockFlyoutDataModel | null;
    isLoading: boolean;
    isLoadingMore: boolean;
    query: string;
    selectedScope: string;
    appliedFilters: DockFlyoutFiltersState;
    draftFilters: DockFlyoutFiltersState;
    isFilterPanelOpen: boolean;
    openActionMenuId: string | null;
    pinnedIds: string[];
    secondaryRowId: string | null;
    openSecondaryGroupId: string | null;
    /** Ids the user has removed locally; filtered out of every selector. */
    deletedIds: string[];
    /** Ids currently selected for a multi-select bulk action. */
    selectedIds: string[];
    /** Anchor for shift+click range selection. */
    lastClickedId: string | null;
    /** Confirmation dialog descriptor — null when hidden. */
    confirm: DockConfirmState | null;
    /** Toast message text — null when hidden. */
    toast: string | null;
    /** CRUD dialog state for rename, delete, share, edit operations */
    crudDialog: {
        isOpen: boolean;
        action?: 'rename' | 'delete' | 'share' | 'edit' | 'download';
        itemId?: string;
        itemType?: 'chats' | 'workspaces' | 'engagements' | 'agents';
        itemName?: string;
        currentTitle?: string;
        agentData?: unknown;
    } | null;
}

export type DockConfirmKind = "bulk-unpinned" | "multi-delete" | "single";

type DockCrudItemType = "chats" | "workspaces" | "engagements" | "agents";

export interface DockConfirmState {
    kind: DockConfirmKind;
    targetId?: string;
}

export interface DockRenameDialogState {
    targetId: string;
    currentName: string;
    draftName: string;
}

type DockRowOptionAction =
    | string
    | {
        id: string;
        label?: string;
    };

type DockExecuteRowActionInput = DockFlyoutRowAction | DockRowOptionAction;

interface DockFlyoutState {
    activeDockButton: DockButtonConfig | null;
    anchorRect: DOMRect | null;
    secondaryAnchorRect: DOMRect | null;
    lastTriggerEl: HTMLElement | null;
    feature: DockFeatureState;
}

type DockFlyoutAction =
    | { type: "REGISTER_TRIGGER"; triggerEl: HTMLElement | null }
    | { type: "SET_ANCHOR_RECT"; anchorRect: DOMRect | null }
    | { type: "SET_SECONDARY_ANCHOR_RECT"; anchorRect: DOMRect | null }
    | {
        type: "OPEN_FLYOUT";
        dockConfig: DockButtonConfig;
        anchorRect: DOMRect | null;
    }
    | { type: "CLOSE_FLYOUT" }
    | { type: "LOAD_DATA_START"; append: boolean }
    | {
        type: "LOAD_DATA_SUCCESS";
        data: DockFlyoutDataModel;
        append: boolean;
    }
    | { type: "LOAD_DATA_ERROR"; append: boolean }
    | { type: "SET_QUERY"; query: string }
    | { type: "SET_SCOPE"; scope: string }
    | { type: "OPEN_FILTER_PANEL" }
    | { type: "CLOSE_FILTER_PANEL" }
    | { type: "APPLY_DRAFT_FILTERS" }
    | { type: "RESET_DRAFT_FILTERS" }
    | {
        type: "SET_FILTER_VALUE";
        sectionId: string;
        value: string;
    }
    | {
        type: "TOGGLE_FILTER_VALUE";
        sectionId: string;
        value: string;
    }
    | {
        type: "TOGGLE_ALL_FILTER_VALUES";
        sectionId: string;
    }
    | { type: "TOGGLE_ROW_MENU"; itemId: string }
    | { type: "CLOSE_ROW_MENU" }
    | { type: "TOGGLE_PIN"; itemId: string }
    | { type: "OPEN_SECONDARY_ROW"; itemId: string; anchorRect: DOMRect | null }
    | { type: "CLOSE_SECONDARY_ROW" }
    | { type: "SET_OPEN_SECONDARY_GROUP"; groupId: string | null }
    | {
        type: "HYDRATE_SECONDARY_GROUPS";
        itemId: string;
        groups: DockFlyoutGroup[];
    }
    | { type: "TOGGLE_SELECTION"; itemId: string }
    | { type: "SET_SELECTION"; ids: string[]; anchorId?: string | null }
    | { type: "CLEAR_SELECTION" }
    | { type: "OPEN_CONFIRM"; confirm: DockConfirmState }
    | { type: "CLOSE_CONFIRM" }
    | { type: "DELETE_ITEMS"; ids: string[]; toast?: string }
    | { type: "SET_TOAST"; toast: string | null }
    // CRUD Dialog Actions
    | { type: "SHOW_RENAME_DIALOG"; itemId: string; itemType: DockCrudItemType; currentTitle: string }
    | { type: "SHOW_DELETE_DIALOG"; itemId: string; itemType: DockCrudItemType; itemName: string }
    | { type: "SHOW_SHARE_DIALOG"; itemId: string; itemType: DockCrudItemType }
    | { type: "SHOW_EDIT_DIALOG"; itemId: string; itemType: DockCrudItemType; agentData?: unknown }
    | { type: "SHOW_DOWNLOAD_DIALOG"; itemId: string; itemType: DockCrudItemType }
    | { type: "HIDE_CRUD_DIALOG" }
    | { type: "UPDATE_ITEM_LABEL"; itemId: string; label: string }
    | { type: "REMOVE_ITEM"; itemId: string };

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const DEBUG_DOCK_FLYOUT = false;

function sanitizeFileNameSegment(value: string): string {
    const slug = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "");

    return slug || "chat-history";
}

function buildChatDownloadFileName(item: DockFlyoutListItem | undefined): string {
    const baseName = item?.name ? sanitizeFileNameSegment(item.name) : "chat-history";
    return `${baseName}.xlsx`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function htmlToPlainText(value: string): string {
    const withLineBreaks = value
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p\s*>/gi, "\n")
        .replace(/<\/li\s*>/gi, "\n");
    const document = new DOMParser().parseFromString(withLineBreaks, "text/html");
    return (document.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

function getNonEmptyText(record: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
        const value = record[key];
        if (typeof value === "string" && value.trim()) {
            return htmlToPlainText(value);
        }
    }

    return "";
}

export function formatChatHistoryForClipboard(payload: unknown): string {
    if (typeof payload === "string") {
        return htmlToPlainText(payload);
    }

    const items = Array.isArray(payload) ? payload : [payload];
    const messages = items.flatMap((item) => {
        if (!isRecord(item)) {
            return [];
        }

        const detail = isRecord(item.ChatDetail)
            ? item.ChatDetail
            : isRecord(item.chatDetail)
                ? item.chatDetail
                : item;
        const question = getNonEmptyText(detail, "question", "Question");
        const answer = getNonEmptyText(detail, "answer", "Answer", "agentResponse", "AgentResponse");
        const error = getNonEmptyText(detail, "error", "Error");

        if (!question && !answer && !error) {
            return [];
        }

        const sections = [
            question ? `Question:\n${question}` : "",
            answer ? `Answer:\n${answer}` : error ? `Error:\n${error}` : "",
        ].filter(Boolean);

        return [sections.join("\n\n")];
    });

    if (messages.length > 0) {
        return messages.join("\n\n---\n\n");
    }

    return JSON.stringify(payload, null, 2) ?? "";
}

async function writeClipboardText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const isCopied = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (!isCopied) {
        throw new Error("Clipboard copy failed");
    }
}

type DockFlyoutDebugContext = Record<string, unknown>;

function logDockFlyoutDebug(event: string, context: DockFlyoutDebugContext = {}): void {
    if (!DEBUG_DOCK_FLYOUT) {
        return;
    }

    console.debug("[useDockFlyout]", event, context);
}

function createInitialFeatureState(): DockFeatureState {
    // Resets all feature-level flyout UI/data state for a fresh open.
    return {
        data: null,
        isLoading: false,
        isLoadingMore: false,
        query: "",
        selectedScope: "all",
        appliedFilters: {},
        draftFilters: {},
        isFilterPanelOpen: false,
        openActionMenuId: null,
        pinnedIds: [],
        secondaryRowId: null,
        openSecondaryGroupId: null,
        deletedIds: [],
        selectedIds: [],
        lastClickedId: null,
        confirm: null,
        toast: null,
        crudDialog: null,
    };
}

const initialState: DockFlyoutState = {
    activeDockButton: null,
    anchorRect: null,
    secondaryAnchorRect: null,
    lastTriggerEl: null,
    feature: createInitialFeatureState(),
};

function buildDefaultFilters(
    data: DockFlyoutDataModel | null,
): DockFlyoutFiltersState {
    // Builds initial filter values from metadata returned by the loader.
    if (!data) {
        return {};
    }

    return data.filterSections.reduce<DockFlyoutFiltersState>((acc, section) => {
        const defaultValue = section.defaultValue;

        if (Array.isArray(defaultValue)) {
            acc[section.id] = [...defaultValue];
            return acc;
        }

        if (typeof defaultValue === "string" && defaultValue.length > 0) {
            acc[section.id] = [defaultValue];
            return acc;
        }

        acc[section.id] = [];
        return acc;
    }, {});
}

function getSectionById(
    data: DockFlyoutDataModel | null,
    sectionId: string,
): DockFlyoutFilterSection | undefined {
    // Helper for filter-related reducer actions.
    return data?.filterSections.find((section) => section.id === sectionId);
}

function areAllCheckboxOptionsSelected(
    selectedValues: string[],
    section?: DockFlyoutFilterSection,
): boolean {
    // Used by "All" checkbox rows in the filter panel.
    if (!section || section.type !== "checkbox") {
        return false;
    }

    return section.options.every((option) => selectedValues.includes(option.id));
}

function mergeUniqueRows(
    currentRows: DockFlyoutListItem[],
    nextRows: DockFlyoutListItem[],
): DockFlyoutListItem[] {
    // Keeps infinite-scroll append stable by preventing duplicate row IDs.
    const currentIds = new Set(currentRows.map((row) => row.id));
    const uniqueNext = nextRows.filter((row) => !currentIds.has(row.id));

    return [...currentRows, ...uniqueNext];
}

function mergeDockFlyoutData(
    currentData: DockFlyoutDataModel | null,
    nextData: DockFlyoutDataModel,
): DockFlyoutDataModel {
    // Merges paginated responses for the active list-based flyout behavior.
    // Grouped/chat data can be reintroduced here later without changing callers.
    if (!currentData || nextData.pagination.page <= 1) {
        return nextData;
    }

    return {
        ...nextData,
        items: mergeUniqueRows(currentData.items, nextData.items),
    };
}

function reconcileFilters(
    filters: DockFlyoutFiltersState,
    data: DockFlyoutDataModel,
    defaults: DockFlyoutFiltersState,
): DockFlyoutFiltersState {
    // Keeps only valid filter keys/options when the server schema changes.
    return data.filterSections.reduce<DockFlyoutFiltersState>((acc, section) => {
        const values = filters[section.id] ?? [];
        const validValues = values.filter((value) =>
            section.options.some((option) => option.id === value),
        );

        if (validValues.length > 0) {
            acc[section.id] =
                section.type === "radio" ? [validValues[0]] : validValues;
            return acc;
        }

        acc[section.id] = defaults[section.id] ?? [];
        return acc;
    }, {});
}

function buildFeatureStateAfterLoadSuccess(
    feature: DockFeatureState,
    incomingData: DockFlyoutDataModel,
    mergedData: DockFlyoutDataModel,
): DockFeatureState {
    const defaults = buildDefaultFilters(incomingData);
    const hasFilterSections = incomingData.filterSections.length > 0;
    const hasAppliedFilters = Object.keys(feature.appliedFilters).length > 0;
    const hasDraftFilters = Object.keys(feature.draftFilters).length > 0;

    const nextAppliedFilters = hasFilterSections
        ? hasAppliedFilters
            ? reconcileFilters(feature.appliedFilters, incomingData, defaults)
            : { ...defaults }
        : {};

    const nextDraftFilters = hasFilterSections
        ? hasDraftFilters
            ? reconcileFilters(feature.draftFilters, incomingData, defaults)
            : { ...nextAppliedFilters }
        : {};

    const activeSecondary =
        feature.secondaryRowId &&
            mergedData.items.some((item) => item.id === feature.secondaryRowId)
            ? mergedData.items.find((item) => item.id === feature.secondaryRowId)
            : null;

    const secondaryGroups = activeSecondary?.secondaryGroups ?? [];
    const openSecondaryGroupId =
        secondaryGroups.length === 0
            ? null
            : feature.openSecondaryGroupId === null
                ? null
                : feature.openSecondaryGroupId &&
                    secondaryGroups.some(
                        (group) => group.id === feature.openSecondaryGroupId,
                    )
                    ? feature.openSecondaryGroupId
                    : secondaryGroups[0]?.id ?? null;

    return {
        ...feature,
        data: mergedData,
        isLoading: false,
        isLoadingMore: false,
        selectedScope: incomingData.scopes.some((scope) => scope.id === feature.selectedScope)
            ? feature.selectedScope
            : incomingData.scopes[0]?.id || "all",
        appliedFilters: nextAppliedFilters,
        draftFilters: nextDraftFilters,
        openSecondaryGroupId,
    };
}

function buildDockLoadParams(
    page: number,
    pageSize: number,
    query: string,
    selectedScope: string,
    appliedFilters: DockFlyoutFiltersState,
): LoadDockFlyoutDataParams {
    // Grouped/chat paging context is intentionally omitted in this revision.
    // Add activeGroupId back here when grouped rendering is re-enabled.
    return {
        page,
        pageSize,
        query,
        scope: selectedScope,
        filters: appliedFilters,
    };
}

function dockFlyoutReducer(
    state: DockFlyoutState,
    action: DockFlyoutAction,
): DockFlyoutState {
    // Centralized state machine for all flyout interaction patterns.
    switch (action.type) {
        case "REGISTER_TRIGGER":
            return {
                ...state,
                lastTriggerEl: action.triggerEl,
            };

        case "SET_ANCHOR_RECT":
            return {
                ...state,
                anchorRect: action.anchorRect,
            };

        case "SET_SECONDARY_ANCHOR_RECT":
            return {
                ...state,
                secondaryAnchorRect: action.anchorRect,
            };

        case "OPEN_FLYOUT":
            return {
                ...state,
                activeDockButton: action.dockConfig,
                anchorRect: action.anchorRect,
                secondaryAnchorRect: null,
                // Opening a flyout always starts with clean feature state.
                feature: createInitialFeatureState(),
            };

        case "CLOSE_FLYOUT":
            return {
                ...state,
                activeDockButton: null,
                anchorRect: null,
                secondaryAnchorRect: null,
                feature: createInitialFeatureState(),
            };

        case "LOAD_DATA_START":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    isLoading: action.append ? state.feature.isLoading : true,
                    isLoadingMore: action.append,
                    openActionMenuId: null,
                },
            };

        case "LOAD_DATA_SUCCESS": {
            // On append we merge; on first page we replace.
            const mergedData = action.append
                ? mergeDockFlyoutData(state.feature.data, action.data)
                : action.data;
            const nextFeatureState = buildFeatureStateAfterLoadSuccess(
                state.feature,
                action.data,
                mergedData,
            );
            const shouldKeepSecondaryAnchor =
                !!nextFeatureState.secondaryRowId &&
                mergedData.items.some((item) => item.id === nextFeatureState.secondaryRowId);

            return {
                ...state,
                feature: nextFeatureState,
                secondaryAnchorRect: shouldKeepSecondaryAnchor
                    ? state.secondaryAnchorRect
                    : null,
            };
        }

        case "LOAD_DATA_ERROR":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    isLoading: false,
                    isLoadingMore: false,
                },
            };

        case "SET_QUERY":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    query: action.query,
                },
            };

        case "SET_SCOPE":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    selectedScope: action.scope,
                },
            };

        case "OPEN_FILTER_PANEL":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    isFilterPanelOpen: true,
                    openActionMenuId: null,
                    draftFilters: { ...state.feature.appliedFilters },
                },
            };

        case "CLOSE_FILTER_PANEL":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    isFilterPanelOpen: false,
                    draftFilters: { ...state.feature.appliedFilters },
                },
            };

        case "APPLY_DRAFT_FILTERS":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    appliedFilters: { ...state.feature.draftFilters },
                    isFilterPanelOpen: false,
                },
            };

        case "RESET_DRAFT_FILTERS": {
            const defaults = buildDefaultFilters(state.feature.data);

            return {
                ...state,
                feature: {
                    ...state.feature,
                    draftFilters: { ...defaults },
                },
            };
        }

        case "SET_FILTER_VALUE":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    draftFilters: {
                        ...state.feature.draftFilters,
                        [action.sectionId]: [action.value],
                    },
                },
            };

        case "TOGGLE_FILTER_VALUE": {
            const current = state.feature.draftFilters[action.sectionId] ?? [];
            const exists = current.includes(action.value);

            return {
                ...state,
                feature: {
                    ...state.feature,
                    draftFilters: {
                        ...state.feature.draftFilters,
                        [action.sectionId]: exists
                            ? current.filter((value) => value !== action.value)
                            : [...current, action.value],
                    },
                },
            };
        }

        case "TOGGLE_ALL_FILTER_VALUES": {
            const section = getSectionById(state.feature.data, action.sectionId);

            if (!section || section.type !== "checkbox") {
                // Only checkbox sections support "select all" behavior.
                return state;
            }

            const current = state.feature.draftFilters[action.sectionId] ?? [];
            const allSelected = areAllCheckboxOptionsSelected(current, section);

            return {
                ...state,
                feature: {
                    ...state.feature,
                    draftFilters: {
                        ...state.feature.draftFilters,
                        [action.sectionId]: allSelected
                            ? []
                            : section.options.map((option) => option.id),
                    },
                },
            };
        }

        case "TOGGLE_ROW_MENU":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    isFilterPanelOpen: false,
                    openActionMenuId:
                        state.feature.openActionMenuId === action.itemId
                            ? null
                            : action.itemId,
                },
            };

        case "CLOSE_ROW_MENU":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    openActionMenuId: null,
                },
            };

        case "TOGGLE_PIN": {
            const exists = state.feature.pinnedIds.includes(action.itemId);

            return {
                ...state,
                feature: {
                    ...state.feature,
                    pinnedIds: exists
                        ? state.feature.pinnedIds.filter((id) => id !== action.itemId)
                        : [action.itemId, ...state.feature.pinnedIds],
                    openActionMenuId: null,
                },
            };
        }

        case "OPEN_SECONDARY_ROW": {
            const selectedItem = state.feature.data?.items.find(
                (item) => item.id === action.itemId,
            );

            return {
                ...state,
                secondaryAnchorRect: action.anchorRect,
                feature: {
                    ...state.feature,
                    secondaryRowId: action.itemId,
                    // Default to first subgroup when secondary opens.
                    openSecondaryGroupId:
                        selectedItem?.secondaryGroups?.[0]?.id ?? state.feature.openSecondaryGroupId,
                    openActionMenuId: null,
                },
            };
        }

        case "CLOSE_SECONDARY_ROW":
            return {
                ...state,
                secondaryAnchorRect: null,
                feature: {
                    ...state.feature,
                    secondaryRowId: null,
                    openSecondaryGroupId: null,
                },
            };

        case "SET_OPEN_SECONDARY_GROUP":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    openSecondaryGroupId: action.groupId,
                },
            };

        case "HYDRATE_SECONDARY_GROUPS": {
            if (!state.feature.data) {
                return state;
            }

            const updatedItems = state.feature.data.items.map((item) =>
                item.id === action.itemId
                    ? { ...item, secondaryGroups: action.groups }
                    : item,
            );

            const currentOpenGroupId = state.feature.openSecondaryGroupId;
            const hasCurrentOpenGroup =
                !!currentOpenGroupId &&
                action.groups.some((group) => group.id === currentOpenGroupId);

            const shouldSetDefaultOpenGroup =
                state.feature.secondaryRowId === action.itemId &&
                action.groups.length > 0 &&
                (!currentOpenGroupId || !hasCurrentOpenGroup);

            return {
                ...state,
                feature: {
                    ...state.feature,
                    data: {
                        ...state.feature.data,
                        items: updatedItems,
                    },
                    openSecondaryGroupId: shouldSetDefaultOpenGroup
                        ? action.groups[0]?.id ?? null
                        : currentOpenGroupId,
                },
            };
        }

        case "TOGGLE_SELECTION": {
            const exists = state.feature.selectedIds.includes(action.itemId);

            return {
                ...state,
                feature: {
                    ...state.feature,
                    selectedIds: exists
                        ? state.feature.selectedIds.filter((id) => id !== action.itemId)
                        : [...state.feature.selectedIds, action.itemId],
                    lastClickedId: action.itemId,
                    openActionMenuId: null,
                },
            };
        }

        case "SET_SELECTION":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    selectedIds: action.ids,
                    lastClickedId:
                        action.anchorId !== undefined
                            ? action.anchorId
                            : state.feature.lastClickedId,
                    openActionMenuId: null,
                },
            };

        case "CLEAR_SELECTION":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    selectedIds: [],
                    lastClickedId: null,
                },
            };

        case "OPEN_CONFIRM":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    confirm: action.confirm,
                    openActionMenuId: null,
                },
            };

        case "CLOSE_CONFIRM":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    confirm: null,
                },
            };

        case "DELETE_ITEMS": {
            const idSet = new Set(action.ids);
            const removedAny = action.ids.some(
                (id) => !state.feature.deletedIds.includes(id),
            );

            return {
                ...state,
                feature: {
                    ...state.feature,
                    deletedIds: Array.from(
                        new Set([...state.feature.deletedIds, ...action.ids]),
                    ),
                    pinnedIds: state.feature.pinnedIds.filter((id) => !idSet.has(id)),
                    selectedIds: state.feature.selectedIds.filter((id) => !idSet.has(id)),
                    confirm: null,
                    toast: removedAny
                        ? action.toast ?? "Items have been deleted"
                        : state.feature.toast,
                },
            };
        }

        case "SET_TOAST":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    toast: action.toast,
                },
            };

        case "SHOW_RENAME_DIALOG":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    crudDialog: {
                        isOpen: true,
                        action: 'rename',
                        itemId: action.itemId,
                        itemType: action.itemType as DockCrudItemType,
                        currentTitle: action.currentTitle,
                    },
                    openActionMenuId: null,
                },
            };

        case "SHOW_DELETE_DIALOG":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    crudDialog: {
                        isOpen: true,
                        action: 'delete',
                        itemId: action.itemId,
                        itemType: action.itemType as DockCrudItemType,
                        itemName: action.itemName,
                    },
                    openActionMenuId: null,
                },
            };

        case "SHOW_SHARE_DIALOG":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    crudDialog: {
                        isOpen: true,
                        action: 'share',
                        itemId: action.itemId,
                        itemType: action.itemType as DockCrudItemType,
                    },
                    openActionMenuId: null,
                },
            };

        case "SHOW_EDIT_DIALOG":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    crudDialog: {
                        isOpen: true,
                        action: 'edit',
                        itemId: action.itemId,
                        itemType: action.itemType as DockCrudItemType,
                        agentData: action.agentData,
                    },
                    openActionMenuId: null,
                },
            };

        case "SHOW_DOWNLOAD_DIALOG":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    crudDialog: {
                        isOpen: true,
                        action: 'download',
                        itemId: action.itemId,
                        itemType: action.itemType as DockCrudItemType,
                    },
                    openActionMenuId: null,
                },
            };

        case "HIDE_CRUD_DIALOG":
            return {
                ...state,
                feature: {
                    ...state.feature,
                    crudDialog: null,
                },
            };

        case "UPDATE_ITEM_LABEL": {
            if (!state.feature.data) return state;
            return {
                ...state,
                feature: {
                    ...state.feature,
                    data: {
                        ...state.feature.data,
                        items: state.feature.data.items.map((item) =>
                            item.id === action.itemId ? { ...item, name: action.label } : item,
                        ),
                    },
                },
            };
        }

        case "REMOVE_ITEM": {
            const nextDeletedIds = Array.from(new Set([...state.feature.deletedIds, action.itemId]));
            const nextSelectedIds = state.feature.selectedIds.filter((id) => id !== action.itemId);
            const nextPinnedIds = state.feature.pinnedIds.filter((id) => id !== action.itemId);
            return {
                ...state,
                feature: {
                    ...state.feature,
                    deletedIds: nextDeletedIds,
                    selectedIds: nextSelectedIds,
                    pinnedIds: nextPinnedIds,
                    // Update items list only when data has already loaded.
                    data: state.feature.data
                        ? {
                            ...state.feature.data,
                            items: state.feature.data.items.filter((item) => item.id !== action.itemId),
                        }
                        : null,
                },
            };
        }

        default:
            return state;
    }
}

function orderRowsWithPins(
    rows: DockFlyoutListItem[],
    pinnedIds: string[],
): DockFlyoutListItem[] {
    // Keeps pinned rows at top while preserving relative order elsewhere.
    const pinnedRows = pinnedIds
        .map((pinnedId) => rows.find((item) => item.id === pinnedId))
        .filter(Boolean) as DockFlyoutListItem[];

    const pinnedIdsSet = new Set(pinnedRows.map((item) => item.id));
    const unpinnedRows = rows.filter((item) => !pinnedIdsSet.has(item.id));

    return [...pinnedRows, ...unpinnedRows];
}

function startOfLocalDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function bucketDockItemByDate(item: DockFlyoutListItem): "today" | "week" | "past30" | "past60" | "past90" | "older" {
    const then = new Date(item.lastUsedAt || item.createdAt || "");
    if (!Number.isFinite(then.getTime())) {
        return "older";
    }

    const todayStart = startOfLocalDay(new Date());
    const thenStart = startOfLocalDay(then);
    const diffDays = Math.floor(
        (todayStart.getTime() - thenStart.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) {
        return "today";
    }
    if (diffDays > 0 && diffDays < CHAT_HISTORY_CONFIG.BUCKET_DAYS_WEEK) {
        return "week";
    }
    if (diffDays > 0 && diffDays <= CHAT_HISTORY_CONFIG.BUCKET_DAYS_PAST30) {
        return "past30";
    }
    if (diffDays > 0 && diffDays <= CHAT_HISTORY_CONFIG.BUCKET_DAYS_PAST60) {
        return "past60";
    }
    if (diffDays > 0 && diffDays <= CHAT_HISTORY_CONFIG.BUCKET_DAYS_PAST90) {
        return "past90";
    }
    return "older";
}

function synthesizeChatGroups(
    rows: DockFlyoutListItem[],
    pinnedIds: string[],
): DockFlyoutGroup[] {
    const itemById = new Map(rows.map((item) => [item.id, item]));
    const pinnedIdSet = new Set(pinnedIds);

    const pinnedFromState = pinnedIds
        .map((id) => itemById.get(id))
        .filter((item): item is DockFlyoutListItem => Boolean(item));

    const pinnedFromData = rows.filter(
        (item) => item.pinned === true && !pinnedIdSet.has(item.id),
    );

    const pinned = [...pinnedFromState, ...pinnedFromData];
    const pinnedItemIds = new Set(pinned.map((item) => item.id));

    const today: DockFlyoutListItem[] = [];
    const week: DockFlyoutListItem[] = [];
    const past30: DockFlyoutListItem[] = [];
    const past60: DockFlyoutListItem[] = [];
    const past90: DockFlyoutListItem[] = [];
    // Ordered map of "YYYY-MM" → items for data older than 90 days
    const monthMap = new Map<string, DockFlyoutListItem[]>();

    rows.forEach((item) => {
        if (pinnedItemIds.has(item.id)) {
            return;
        }

        const bucket = bucketDockItemByDate(item);
        if (bucket === "today") {
            today.push(item);
        } else if (bucket === "week") {
            week.push(item);
        } else if (bucket === "past30") {
            past30.push(item);
        } else if (bucket === "past60") {
            past60.push(item);
        } else if (bucket === "past90") {
            past90.push(item);
        } else {
            // "older" — group by calendar month of the item's date
            const date = new Date(item.lastUsedAt || item.createdAt || "");
            if (Number.isFinite(date.getTime())) {
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                if (!monthMap.has(key)) {
                    monthMap.set(key, []);
                }
                monthMap.get(key)!.push(item);
            }
        }
    });

    const groups: DockFlyoutGroup[] = [];

    if (pinned.length > 0) {
        groups.push({ id: "pinned", label: "Pinned", items: pinned });
    }

    groups.push(
        { id: CHAT_HISTORY_CONFIG.GROUP_ID_TODAY, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_TODAY, items: today },
        { id: CHAT_HISTORY_CONFIG.GROUP_ID_THIS_WEEK, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_THIS_WEEK, items: week },
        { id: CHAT_HISTORY_CONFIG.GROUP_ID_PAST_30, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_PAST_30, items: past30 },
    );

    // past-60 and past-90 headings are only shown when there is data for that range
    if (past60.length > 0) {
        groups.push({ id: CHAT_HISTORY_CONFIG.GROUP_ID_PAST_60, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_PAST_60, items: past60 });
    }
    if (past90.length > 0) {
        groups.push({ id: CHAT_HISTORY_CONFIG.GROUP_ID_PAST_90, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_PAST_90, items: past90 });
    }

    // Monthly groups for data older than 90 days, sorted newest month first
    const sortedMonthKeys = Array.from(monthMap.keys()).sort((a, b) => b.localeCompare(a));
    for (const key of sortedMonthKeys) {
        const monthItems = monthMap.get(key)!;
        const [year, month] = key.split("-");
        const label = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(undefined, {
            month: "long",
            year: "numeric",
        });
        groups.push({ id: `month-${key}`, label, items: monthItems });
    }

    return groups;
}

function selectListItems(
    data: DockFlyoutDataModel | null,
    pinnedIds: string[],
    deletedIds: string[],
    flyoutKind?: DockFlyoutKind,
): DockFlyoutListItem[] {
    if (!data) {
        return [];
    }

    const deletedSet = new Set(deletedIds);
    const surviving = data.items.filter((item) => !deletedSet.has(item.id));
    const aggregateRows = surviving.filter((item) => item.isAggregate);
    const normalRows = surviving.filter((item) => !item.isAggregate);
    const effectiveGroups = flyoutKind === "chats"
        ? synthesizeChatGroups(normalRows, pinnedIds)
        : data.groups;

    if (effectiveGroups.length > 0) {
        const groupedRows: DockFlyoutListItem[] = [];
        const fallbackItem = normalRows[0];
        const keepEmptyHeaders = flyoutKind === "chats";

        effectiveGroups.forEach((group) => {
            const groupItems = group.items.filter((item) => !deletedSet.has(item.id));
            if (groupItems.length === 0 && !keepEmptyHeaders) {
                return;
            }

            const firstGroupItem = groupItems[0] ?? fallbackItem;
            if (!firstGroupItem) {
                return;
            }

            groupedRows.push({
                id: `__group__${group.id}`,
                name: group.label,
                appId: firstGroupItem.appId,
                pageId: firstGroupItem.pageId,
                navigationMode: "direct",
                isAggregate: true,
                filters: {
                    __groupId: group.id,
                },
                optionsMenu: {
                    show: false,
                    actions: [],
                },
            });

            groupedRows.push(
                ...groupItems.map((item) => ({
                    ...item,
                    filters: {
                        ...(item.filters ?? {}),
                        __groupId: group.id,
                    },
                })),
            );
        });

        return [...aggregateRows, ...groupedRows];
    }

    return [...aggregateRows, ...orderRowsWithPins(normalRows, pinnedIds)];
}

/**
 * Returns a copy of `data` where pinned items are lifted out of their
 * date-bucket groups and collected into a leading "Pinned" group.
 * Items already marked `pinned` in the raw data are treated the same
 * as items whose id is in `pinnedIds`.
 */
function selectDisplayData(
    data: DockFlyoutDataModel | null,
    pinnedIds: string[],
    deletedIds: string[],
): DockFlyoutDataModel | null {
    if (!data) return null;

    const deletedSet = new Set(deletedIds);
    const dataAfterDelete = deletedSet.size === 0
        ? data
        : {
            ...data,
            items: data.items.filter((item) => !deletedSet.has(item.id)),
            groups: data.groups
                .map((group) => ({
                    ...group,
                    items: group.items.filter((item) => !deletedSet.has(item.id)),
                }))
                .filter((group) => group.items.length > 0),
        };

    if (pinnedIds.length === 0 && !dataAfterDelete.items.some((i) => i.pinned)) {
        return dataAfterDelete;
    }

    const itemById = new Map(dataAfterDelete.items.map((item) => [item.id, item]));
    const pinnedIdSet = new Set(pinnedIds);

    // Preserve explicit pin recency order (newly pinned first), then include backend-pinned rows.
    const pinnedItemsFromState = pinnedIds
        .map((id) => itemById.get(id))
        .filter((item): item is DockFlyoutListItem => Boolean(item))
        .map((item) => ({ ...item, pinned: true }));

    const pinnedItemsFromData = dataAfterDelete.items
        .filter((item) => item.pinned && !pinnedIdSet.has(item.id))
        .map((item) => ({ ...item, pinned: true }));

    const pinnedItems: DockFlyoutListItem[] = [
        ...pinnedItemsFromState,
        ...pinnedItemsFromData,
    ];

    if (pinnedItems.length === 0) return dataAfterDelete;

    // Strip pinned items from every existing group.
    const filteredGroups = dataAfterDelete.groups
        .map((group) => ({
            ...group,
            items: group.items.filter(
                (item) => !pinnedIdSet.has(item.id) && !item.pinned,
            ),
        }))
        .filter((group) => group.items.length > 0);

    return {
        ...dataAfterDelete,
        groups: [
            { id: "pinned", label: "Pinned", items: pinnedItems },
            ...filteredGroups,
        ],
    };
}

function selectActiveSecondaryItem(
    rows: DockFlyoutListItem[],
    secondaryRowId: string | null,
): DockFlyoutListItem | null {
    if (!secondaryRowId) {
        return null;
    }

    return rows.find((item) => item.id === secondaryRowId) ?? null;
}

function selectDropdownOptions(
    data: DockFlyoutDataModel | null,
    selectedScope: string,
): Array<{ id: string; label: string; isActive: boolean }> {
    const scopes = data?.scopes ?? [];

    return scopes.map((scope) => ({
        id: scope.id,
        label: scope.label,
        isActive: scope.id === selectedScope,
    }));
}

function selectActiveDropdownLabel(
    data: DockFlyoutDataModel | null,
    selectedScope: string,
): string {
    const scopes = data?.scopes ?? [];

    return (
        scopes.find((scope) => scope.id === selectedScope)?.label ??
        scopes[0]?.label ??
        ""
    );
}

const createSetQueryAction = (query: string): DockFlyoutAction => ({
    type: "SET_QUERY",
    query,
});

const createSetScopeAction = (scope: string): DockFlyoutAction => ({
    type: "SET_SCOPE",
    scope,
});

const createToggleRowMenuAction = (itemId: string): DockFlyoutAction => ({
    type: "TOGGLE_ROW_MENU",
    itemId,
});

const createCloseRowMenuAction = (): DockFlyoutAction => ({
    type: "CLOSE_ROW_MENU",
});

const createOpenFilterPanelAction = (): DockFlyoutAction => ({
    type: "OPEN_FILTER_PANEL",
});

const createCloseFilterPanelAction = (): DockFlyoutAction => ({
    type: "CLOSE_FILTER_PANEL",
});

const createApplyDraftFiltersAction = (): DockFlyoutAction => ({
    type: "APPLY_DRAFT_FILTERS",
});

const createResetDraftFiltersAction = (): DockFlyoutAction => ({
    type: "RESET_DRAFT_FILTERS",
});

const createSetFilterValueAction = (
    sectionId: string,
    value: string,
): DockFlyoutAction => ({
    type: "SET_FILTER_VALUE",
    sectionId,
    value,
});

const createToggleFilterValueAction = (
    sectionId: string,
    value: string,
): DockFlyoutAction => ({
    type: "TOGGLE_FILTER_VALUE",
    sectionId,
    value,
});

const createToggleAllFilterValuesAction = (sectionId: string): DockFlyoutAction => ({
    type: "TOGGLE_ALL_FILTER_VALUES",
    sectionId,
});

const createOpenSecondaryRowAction = (
    itemId: string,
    anchorRect: DOMRect | null,
): DockFlyoutAction => ({
    type: "OPEN_SECONDARY_ROW",
    itemId,
    anchorRect,
});

const createCloseSecondaryRowAction = (): DockFlyoutAction => ({
    type: "CLOSE_SECONDARY_ROW",
});

const createSetOpenSecondaryGroupAction = (
    groupId: string | null,
): DockFlyoutAction => ({
    type: "SET_OPEN_SECONDARY_GROUP",
    groupId,
});

export interface UseDockFlyoutResult {
    activeDockButton: DockButtonConfig | null;
    anchorRect: DOMRect | null;
    secondaryAnchorRect: DOMRect | null;
    isOpen: boolean;
    flyoutKind: DockFlyoutKind | undefined;
    flyoutConfig: DockFlyoutConfig | undefined;
    flyoutTitle: string;

    registerTriggerClick: (triggerEl: HTMLElement | null) => void;
    openFlyout: (dockConfig: DockButtonConfig) => void;
    toggleFlyout: (dockConfig: DockButtonConfig) => void;
    closeFlyout: () => void;

    query: string;
    setQuery: (value: string) => void;
    clearQuery: () => void;

    selectedScope: string;
    setSelectedScope: (value: string) => void;

    data: DockFlyoutDataModel | null;
    displayData: DockFlyoutDataModel | null;
    isLoading: boolean;
    isLoadingMore: boolean;
    hasMore: boolean;
    loadNextPage: () => void;
    listItems: DockFlyoutListItem[];
    activeDropdownLabel: string;
    dropdownOptions: Array<{
        id: string;
        label: string;
        isActive: boolean;
    }>;

    filterSections: DockFlyoutFilterSection[];
    appliedFilters: DockFlyoutFiltersState;
    draftFilters: DockFlyoutFiltersState;

    isFilterPanelOpen: boolean;
    openFilterPanel: () => void;
    closeFilterPanel: () => void;
    applyDraftFilters: () => void;
    resetDraftFilters: () => void;
    setFilterValue: (sectionId: string, value: string) => void;
    toggleFilterValue: (sectionId: string, value: string) => void;
    toggleAllFilterValues: (sectionId: string) => void;
    areAllFilterValuesSelected: (sectionId: string) => boolean;

    openActionMenuId: string | null;
    toggleRowMenu: (itemId: string) => void;
    closeRowMenu: () => void;
    navigateToItem: (item: DockFlyoutListItem) => void;
    togglePin: (itemId: string) => void;
    executeRowAction: (itemId: string, action: DockExecuteRowActionInput) => void;
    pinnedIds: string[];

    activeSecondaryItem: DockFlyoutListItem | null;
    openSecondaryForRow: (itemId: string, anchorRect: DOMRect | null) => void;
    closeSecondary: () => void;
    openSecondaryGroupId: string | null;
    setOpenSecondaryGroupId: (groupId: string | null) => void;
    syncWorkspaceObjectives: () => Promise<void>;

    executeHeaderAction: (
        action: DockFlyoutHeaderAction,
        optionId?: string,
    ) => void;

    // Multi-select + bulk actions state.
    selectedIds: string[];
    toggleSelection: (itemId: string) => void;
    extendSelectionTo: (itemId: string) => void;
    clearSelection: () => void;
    exportSelection: () => void;

    // Confirmation modal + toast state.
    confirm: DockConfirmState | null;
    openConfirm: (confirm: DockConfirmState) => void;
    closeConfirm: () => void;
    confirmDelete: () => void;

    renameDialog: DockRenameDialogState | null;
    setRenameDraftName: (name: string) => void;
    closeRenameDialog: () => void;
    submitRename: () => void;
    isRenaming: boolean;

    toast: string | null;
    dismissToast: () => void;

    getGroupedRenderHints?: (input: {
        groupHeaderIds: string[];
        collapsedGroupIds: string[];
        query: string;
    }) => {
        defaultCollapsedGroupIds?: string[];
        pinnedFooterGroupIds?: string[];
        collapseOthersOnExpand?: boolean;
    };
    // CRUD Dialog Management
    crudDialog: {
        isOpen: boolean;
        action?: 'rename' | 'delete' | 'share' | 'edit' | 'download';
        itemId?: string;
        itemType?: DockCrudItemType;
        itemName?: string;
        currentTitle?: string;
        agentData?: unknown;
    } | null;
    showRenameDialog: (itemId: string, itemType: DockCrudItemType, currentTitle: string) => void;
    showDeleteDialog: (itemId: string, itemType: DockCrudItemType, itemName: string) => void;
    showShareDialog: (itemId: string, itemType: DockCrudItemType) => void;
    showEditDialog: (itemId: string, itemType: DockCrudItemType, agentData?: unknown) => void;
    showDownloadDialog: (itemId: string, itemType: DockCrudItemType) => void;
    hideCrudDialog: () => void;
    notifyItemRenamed: (itemId: string, label: string) => void;
    notifyItemDeleted: (itemId: string) => void;
    /** Reload page 1 of the active flyout kind if the flyout is open. */
    refreshData: () => void;
}

export interface UseDockFlyoutOptions {
    onWorkspaceItemSelect?: (item: DockFlyoutListItem) => boolean | void;
    onWorkspaceSecondaryItemSelect?: (payload: {
        workspace: DockFlyoutListItem;
        child: DockFlyoutListItem;
    }) => boolean | void;
    onChatItemSelect?: (item: DockFlyoutListItem) => boolean | void;
    onAgentItemSelect?: (item: DockFlyoutListItem) => boolean | void;
    onNewChatActionSelect?: () => boolean | void;
    onNewAgentActionSelect?: () => boolean | void;
}

export function useDockFlyout(options?: UseDockFlyoutOptions): UseDockFlyoutResult {
    // Public controller hook consumed by AppShell and DockFlyout renderer.
    const [state, dispatch] = useReducer(dockFlyoutReducer, initialState);
    const requestIdRef = useRef(0);
    const secondaryLoadInFlightRef = useRef<Set<string>>(new Set());
    const secondaryRequestTokenRef = useRef(0);
    const workspaceSyncInFlightRef = useRef<Set<string>>(new Set());
    const latestSecondaryRequestedItemIdRef = useRef<string | null>(null);
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [renameDialog, setRenameDialog] = useState<DockRenameDialogState | null>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const { navigateToPage } = useNavigation();
    const { setEngagement } = useEngagement();

    const activeDockButton = state.activeDockButton;
    const flyoutKind = activeDockButton?.kind;
    const flyoutConfig = activeDockButton?.flyout;
    const isOpen = !!activeDockButton;

    const effectiveFlyoutConfig = useMemo(() => {
        if (!flyoutConfig) {
            return flyoutConfig;
        }

        if (flyoutKind === "chats") {
            return {
                ...flyoutConfig,
                structure: "grouped" as const,
            };
        }

        return flyoutConfig;
    }, [flyoutConfig, flyoutKind]);

    const pageSize = effectiveFlyoutConfig?.pageSize ?? DEFAULT_PAGE_SIZE;

    // Stable serialized key used to trigger reload when applied filters truly change.
    const appliedFiltersKey = useMemo(
        () => JSON.stringify(state.feature.appliedFilters),
        [state.feature.appliedFilters],
    );

    // Keep an equivalent filter object reference stable across renders to avoid
    // retriggering loads when reducer emits a new object with unchanged values.
    const stableAppliedFilters = useMemo(
        () => state.feature.appliedFilters,
        [appliedFiltersKey],
    );

    useEffect(() => {
    const handleDockFlyoutClosed = () => {
        setRenameDialog(null);
        setIsRenaming(false);
    };

    window.addEventListener(
        "kci:dockflyout-closed",
        handleDockFlyoutClosed,
    );

    return () => {
        window.removeEventListener(
        "kci:dockflyout-closed",
        handleDockFlyoutClosed,
        );
    };
    }, []);
    const registerTriggerClick = useCallback((triggerEl: HTMLElement | null) => {
        // Stores trigger element so flyout can position itself near the clicked button.
        dispatch({ type: "REGISTER_TRIGGER", triggerEl });
    }, []);

    const openFlyout = useCallback(
        (dockConfig: DockButtonConfig) => {
            // Opens requested flyout and snapshots current trigger location.
            dispatch({
                type: "OPEN_FLYOUT",
                dockConfig,
                anchorRect: state.lastTriggerEl?.getBoundingClientRect() ?? null,
            });
        },
        [state.lastTriggerEl],
    );

    const toggleFlyout = useCallback(
        (dockConfig: DockButtonConfig) => {
            // Clicking the same dock button toggles flyout visibility.
            const isSameFlyout =
                state.activeDockButton?.id === dockConfig.id &&
                state.activeDockButton?.homePage === dockConfig.homePage;

            if (isSameFlyout) {
                dispatch({ type: "CLOSE_FLYOUT" });
                return;
            }

            dispatch({
                type: "OPEN_FLYOUT",
                dockConfig,
                anchorRect: state.lastTriggerEl?.getBoundingClientRect() ?? null,
            });
        },
        [state.activeDockButton, state.lastTriggerEl],
    );

    const closeFlyout = useCallback(() => {
        // Explicit close path used by outside-click, escape, and navigation.
         window.dispatchEvent(
            new CustomEvent("kci:dockflyout-closed"),
        );
        logDockFlyoutDebug("close_flyout");
        dispatch({ type: "CLOSE_FLYOUT" });
    }, []);

    const requestDockFlyoutPage = useCallback(
        async (
            append: boolean,
            loadParams: LoadDockFlyoutDataParams,
        ) => {
            if (!flyoutKind) {
                logDockFlyoutDebug("skip_request_missing_kind", { append, loadParams });
                return;
            }

            const requestId = ++requestIdRef.current;
            logDockFlyoutDebug("request_start", {
                requestId,
                append,
                flyoutKind,
                page: loadParams.page,
                pageSize: loadParams.pageSize,
                query: loadParams.query,
                scope: loadParams.scope,
            });
            dispatch({ type: "LOAD_DATA_START", append });

            try {
                const data = await loadDockFlyoutData(flyoutKind, loadParams);

                if (requestId !== requestIdRef.current) {
                    logDockFlyoutDebug("request_stale_ignored", {
                        requestId,
                        latestRequestId: requestIdRef.current,
                        flyoutKind,
                    });
                    return;
                }

                logDockFlyoutDebug("request_success", {
                    requestId,
                    append,
                    flyoutKind,
                    items: data.items.length,
                    hasMore: data.pagination.hasMore,
                    page: data.pagination.page,
                });
                dispatch({
                    type: "LOAD_DATA_SUCCESS",
                    data,
                    append,
                });
            } catch (error) {
                console.error(
                    append
                        ? `Failed to load more ${flyoutKind} dock flyout data`
                        : `Failed to load ${flyoutKind} dock flyout data`,
                    error,
                );
                logDockFlyoutDebug("request_error", {
                    requestId,
                    append,
                    flyoutKind,
                    message: error instanceof Error ? error.message : String(error),
                });

                if (requestId === requestIdRef.current) {
                    dispatch({ type: "LOAD_DATA_ERROR", append });
                }
            }
        },
        [flyoutKind],
    );

    useEffect(() => {
        // Debounces query to avoid reloading data for every keystroke.
        const timerId = window.setTimeout(() => {
            setDebouncedQuery(state.feature.query);
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timerId);
        };
    }, [state.feature.query]);

    useEffect(() => {
        // Recomputes anchor when viewport/layout changes so flyout stays aligned.
        if (!state.activeDockButton || !state.lastTriggerEl) {
            return;
        }

        // Recomputes the anchor rectangle from the most recent trigger element.
        const updateAnchorRect = () => {
            dispatch({
                type: "SET_ANCHOR_RECT",
                anchorRect: state.lastTriggerEl?.getBoundingClientRect() ?? null,
            });
        };

        updateAnchorRect();

        window.addEventListener("resize", updateAnchorRect);
        window.addEventListener("scroll", updateAnchorRect, true);

        return () => {
            window.removeEventListener("resize", updateAnchorRect);
            window.removeEventListener("scroll", updateAnchorRect, true);
        };
    }, [state.activeDockButton, state.lastTriggerEl]);

    useEffect(() => {
        // Loads first page whenever kind/query/scope/filter context changes.
        if (!isOpen || !flyoutKind) {
            return;
        }

        void requestDockFlyoutPage(
            false,
            buildDockLoadParams(
                1,
                pageSize,
                debouncedQuery,
                state.feature.selectedScope,
                stableAppliedFilters,
            ),
        );
    }, [
        isOpen,
        flyoutKind,
        debouncedQuery,
        pageSize,
        state.feature.selectedScope,
        stableAppliedFilters,
        requestDockFlyoutPage,
    ]);

    const loadNextPage = useCallback(() => {
        // Infinite-scroll entry point for appending next page.
        if (!isOpen || !flyoutKind || !state.feature.data) {
            logDockFlyoutDebug("skip_next_page", {
                reason: "missing_context",
                isOpen,
                flyoutKind,
                hasData: !!state.feature.data,
            });
            return;
        }

        if (state.feature.isLoading || state.feature.isLoadingMore) {
            // Prevent overlapping requests while a load is already running.
            logDockFlyoutDebug("skip_next_page", {
                reason: "request_in_flight",
                isLoading: state.feature.isLoading,
                isLoadingMore: state.feature.isLoadingMore,
            });
            return;
        }

        if (!state.feature.data.pagination.hasMore) {
            // Stop when backend/mock indicates there are no more rows.
            logDockFlyoutDebug("skip_next_page", {
                reason: "no_more_data",
                currentPage: state.feature.data.pagination.page,
            });
            return;
        }

        const currentData = state.feature.data;

        void requestDockFlyoutPage(
            true,
            buildDockLoadParams(
                currentData.pagination.page + 1,
                currentData.pagination.pageSize || pageSize,
                debouncedQuery,
                state.feature.selectedScope,
                stableAppliedFilters,
            ),
        );
    }, [
        isOpen,
        flyoutKind,
        pageSize,
        debouncedQuery,
        state.feature.data,
        state.feature.selectedScope,
        stableAppliedFilters,
        state.feature.isLoading,
        state.feature.isLoadingMore,
        requestDockFlyoutPage,
    ]);

    const listItems = useMemo(() => {
        // Flat/nested list source with aggregate fixed rows and pinned ordering.
        return selectListItems(
            state.feature.data,
            state.feature.pinnedIds,
            state.feature.deletedIds,
            flyoutKind,
        );
    }, [flyoutKind, state.feature.data, state.feature.pinnedIds, state.feature.deletedIds]);

    const displayData = useMemo(() => {
        // For grouped mode: injects a leading "Pinned" group when there are pinned items.
        return selectDisplayData(
            state.feature.data,
            state.feature.pinnedIds,
            state.feature.deletedIds,
        );
    }, [state.feature.data, state.feature.pinnedIds, state.feature.deletedIds]);

    const activeSecondaryItem = useMemo(() => {
        // Resolves currently hovered/selected primary row for nested secondary panel.
        const item = selectActiveSecondaryItem(listItems, state.feature.secondaryRowId);

        if (!item && state.feature.secondaryRowId) {
            logDockFlyoutDebug("secondary_item_missing", {
                secondaryRowId: state.feature.secondaryRowId,
                listSize: listItems.length,
            });
        }

        return item;
    }, [listItems, state.feature.secondaryRowId]);

    const activeDropdownLabel = useMemo(() => {
        // Human-readable label for scope dropdown trigger.
        return selectActiveDropdownLabel(state.feature.data, state.feature.selectedScope);
    }, [state.feature.data, state.feature.selectedScope]);

    const dropdownOptions = useMemo(() => {
        // Scope dropdown options with active marker.
        return selectDropdownOptions(state.feature.data, state.feature.selectedScope);
    }, [state.feature.data, state.feature.selectedScope]);

    const flyoutTitle =
        state.feature.data?.title ?? effectiveFlyoutConfig?.title ?? "Dock Flyout";

    const getGroupedRenderHints = useCallback(
        (input: {
            groupHeaderIds: string[];
            collapsedGroupIds: string[];
            query: string;
        }) => {
            if (flyoutKind !== "chats") {
                return {
                    collapseOthersOnExpand: true,
                };
            }

            const validGroupIds = new Set(input.groupHeaderIds);
            const defaultCollapsedGroupIds = Array.from(new Set([
                ...CHAT_HISTORY_CONFIG.DEFAULT_COLLAPSED_GROUP_IDS,
                ...input.groupHeaderIds.filter((id) => id.startsWith("month-")),
            ])).filter((id) => validGroupIds.has(id));

            return {
                defaultCollapsedGroupIds,
                // Keep chat sections in place when collapsed to avoid visible jumps.
                pinnedFooterGroupIds: [],
                collapseOthersOnExpand: true,
            };
        },
        [flyoutKind],
    );

    const areAllFilterValuesSelected = useCallback(
        (sectionId: string) => {
            // Drives "All" checkbox state for multi-select sections.
            const section = getSectionById(state.feature.data, sectionId);
            const selected = state.feature.draftFilters[sectionId] ?? [];

            return areAllCheckboxOptionsSelected(selected, section);
        },
        [state.feature.data, state.feature.draftFilters],
    );

    const togglePin = useCallback((itemId: string) => {
        // Toggles pin state used by row ordering in flat/grouped/nested lists.
        dispatch({ type: "TOGGLE_PIN", itemId });
    }, []);

    const navigateToItem = useCallback(
        (item: DockFlyoutListItem) => {
            // Central row-click navigation path: cleanup transient UI then navigate.

            if (
                state.activeDockButton?.kind === "agents" &&
                item.navigationMode === "direct" &&
                options?.onAgentItemSelect
            ) {
                const handled = options.onAgentItemSelect(item);

                if (handled !== false) {
                    setEngagement(null);
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    dispatch({ type: "CLOSE_FILTER_PANEL" });
                    dispatch({ type: "CLOSE_SECONDARY_ROW" });
                    dispatch({ type: "CLOSE_FLYOUT" });
                    navigateToPage(item.appId, item.pageId ?? CONFIG.FIXED_TAB_HOME);
                    return;
                }
            }

            if (
                state.activeDockButton?.kind === "chats" &&
                item.navigationMode === "direct" &&
                options?.onChatItemSelect
            ) {
                const handled = options.onChatItemSelect(item);

                if (handled !== false) {
                    setEngagement(null);
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    dispatch({ type: "CLOSE_FILTER_PANEL" });
                    dispatch({ type: "CLOSE_SECONDARY_ROW" });
                    dispatch({ type: "CLOSE_FLYOUT" });
                    navigateToPage(item.appId, item.pageId ?? CONFIG.FIXED_TAB_HOME);
                    return;
                }
            }

            if (
                state.activeDockButton?.kind === "workspaces" &&
                item.navigationMode === "direct" &&
                options?.onWorkspaceItemSelect
            ) {
                const handled = options.onWorkspaceItemSelect(item);

                if (handled !== false) {
                    setEngagement(null);
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    dispatch({ type: "CLOSE_FILTER_PANEL" });
                    dispatch({ type: "CLOSE_SECONDARY_ROW" });
                    dispatch({ type: "CLOSE_FLYOUT" });
                    return;
                }
            }

            const workspaceSecondaryParent =
                state.activeDockButton?.kind === "workspaces"
                    ? activeSecondaryItem ?? null
                    : null;

            if (
                workspaceSecondaryParent &&
                item.navigationMode === "direct" &&
                options?.onWorkspaceSecondaryItemSelect
            ) {
                const handled = options.onWorkspaceSecondaryItemSelect({
                    workspace: workspaceSecondaryParent,
                    child: item,
                });

                if (handled !== false) {
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    dispatch({ type: "CLOSE_FILTER_PANEL" });
                    dispatch({ type: "CLOSE_SECONDARY_ROW" });
                    dispatch({ type: "CLOSE_FLYOUT" });
                    return;
                }
            }

            setEngagement(null);
            dispatch({ type: "CLOSE_ROW_MENU" });
            dispatch({ type: "CLOSE_FILTER_PANEL" });
            dispatch({ type: "CLOSE_SECONDARY_ROW" });
            dispatch({ type: "CLOSE_FLYOUT" });
            navigateToPage(item.appId, item.pageId ?? CONFIG.FIXED_TAB_HOME);
        },
        [activeSecondaryItem, navigateToPage, options, setEngagement, state.activeDockButton?.kind],
    );

    const executeRowAction = useCallback(
        (itemId: string, action: DockExecuteRowActionInput) => {
            // Pin/delete/rename mutate list state; copy/download call chat history APIs.
            const actionId = typeof action === "string" ? action : action.id;
            const chatItem = listItems.find((row) => row.id === itemId);

            if ((actionId === "pin" || actionId === "unpin") && flyoutKind === "chats") {
                if (!chatItem || chatItem.isAggregate) {
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    return;
                }

                dispatch({ type: "CLOSE_ROW_MENU" });
                void updateChatSessionPin(itemId, !chatItem.pinned)
                    .then((updated) => {
                        if (!updated) {
                            throw new Error("Chat pin update was not accepted");
                        }

                        return requestDockFlyoutPage(
                            false,
                            buildDockLoadParams(
                                1,
                                state.feature.data?.pagination.pageSize || pageSize,
                                debouncedQuery,
                                state.feature.selectedScope,
                                state.feature.appliedFilters,
                            ),
                        );
                    })
                    .catch((error) => {
                        console.error("[useDockFlyout] chat pin update failed", error);
                        dispatch({
                            type: "SET_TOAST",
                            toast: "Unable to update chat pin. Please try again.",
                        });
                    });
                return;
            }

            if (actionId === "pin" || actionId === "unpin") {
                dispatch({ type: "TOGGLE_PIN", itemId });
                return;
            }

            if (actionId === "delete") {
                dispatch({
                    type: "OPEN_CONFIRM",
                    confirm: { kind: "single", targetId: itemId },
                });
                return;
            }

            if (actionId === "copy" && flyoutKind === "chats") {
                if (!chatItem || chatItem.isAggregate) {
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    return;
                }

                dispatch({ type: "CLOSE_ROW_MENU" });

                void getChatSession(itemId)
                    .then((chatSession) => writeClipboardText(formatChatHistoryForClipboard(chatSession)))
                    .then(() => {
                        dispatch({ type: "SET_TOAST", toast: "Copied chat history to clipboard." });
                    })
                    .catch((error) => {
                        console.error("[useDockFlyout] chat copy failed", error);
                        dispatch({
                            type: "SET_TOAST",
                            toast: "Copy failed. Please try again.",
                        });
                    });

                return;
            }

            if (actionId === "download" && flyoutKind === "chats") {
                if (!chatItem || chatItem.isAggregate) {
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    return;
                }

                dispatch({ type: "CLOSE_ROW_MENU" });
                dispatch({ type: "SET_TOAST", toast: "Preparing chat download..." });

                void downloadChat(itemId, buildChatDownloadFileName(chatItem))
                    .then(() => {
                        dispatch({ type: "SET_TOAST", toast: "Chat download started." });
                    })
                    .catch((error) => {
                        console.error("[useDockFlyout] chat download failed", error);
                        dispatch({
                            type: "SET_TOAST",
                            toast: "Download failed. Please try again.",
                        });
                    });

                return;
            }

            if (actionId === "rename" && flyoutKind === "chats") {
                if (!chatItem || chatItem.isAggregate) {
                    dispatch({ type: "CLOSE_ROW_MENU" });
                    return;
                }

                const currentName = chatItem.name?.trim() ?? "";

                dispatch({ type: "CLOSE_ROW_MENU" });
                setRenameDialog({
                    targetId: itemId,
                    currentName,
                    draftName: currentName,
                });

                return;
            }

            dispatch({ type: "CLOSE_ROW_MENU" });
        },
        [
            debouncedQuery,
            flyoutKind,
            listItems,
            pageSize,
            requestDockFlyoutPage,
            state.feature.appliedFilters,
            state.feature.data?.pagination.pageSize,
            state.feature.selectedScope,
        ],
    );

    const setRenameDraftName = useCallback((name: string) => {
        setRenameDialog((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                draftName: name,
            };
        });
    }, []);

    const closeRenameDialog = useCallback(() => {
        if (isRenaming) {
            return;
        }

        setRenameDialog(null);
    }, [isRenaming]);

    const submitRename = useCallback(() => {
        if (!renameDialog || isRenaming) {
            return;
        }

        const nextName = renameDialog.draftName.trim();
        const currentName = renameDialog.currentName.trim();

        if (!nextName) {
            dispatch({
                type: "SET_TOAST",
                toast: "Name cannot be empty.",
            });
            return;
        }

        if (nextName === currentName) {
            setRenameDialog(null);
            return;
        }

        setIsRenaming(true);

        void renameChatSession(renameDialog.targetId, nextName)
            .then((renamed) => {
                if (!renamed) {
                    dispatch({
                        type: "SET_TOAST",
                        toast: "Rename failed. Please try again.",
                    });
                    return;
                }

                setRenameDialog(null);
                dispatch({
                    type: "SET_TOAST",
                    toast: "Chat renamed successfully.",
                });

                window.dispatchEvent(
                    new CustomEvent('kci-chat-history-session-renamed', {
                        detail: {
                            sessionId: renameDialog.targetId,
                            title: nextName,
                        },
                    }),
                );

                void requestDockFlyoutPage(
                    false,
                    buildDockLoadParams(
                        1,
                        state.feature.data?.pagination.pageSize || pageSize,
                        debouncedQuery,
                        state.feature.selectedScope,
                        state.feature.appliedFilters,
                    ),
                );
            })
            .catch((error) => {
                console.error("[useDockFlyout] chat rename failed", error);
                dispatch({
                    type: "SET_TOAST",
                    toast: "Rename failed. Please try again.",
                });
            })
            .finally(() => {
                setIsRenaming(false);
            });
    }, [
        debouncedQuery,
        isRenaming,
        pageSize,
        renameDialog,
        requestDockFlyoutPage,
        state.feature.appliedFilters,
        state.feature.data?.pagination.pageSize,
        state.feature.selectedScope,
    ]);

    const executeHeaderAction = useCallback(
        (action: DockFlyoutHeaderAction, optionId?: string) => {
            // Header action dispatcher supports navigate, bulk chat actions, and modal/custom later.
            if (
                action.id === "new-chat" &&
                state.activeDockButton?.kind === "chats" &&
                options?.onNewChatActionSelect
            ) {
                const handled = options.onNewChatActionSelect();
                if (handled !== false) {
                    closeFlyout();
                    return;
                }
            }

            if (
                action.id === "new-agent" &&
                state.activeDockButton?.kind === "agents" &&
                options?.onNewAgentActionSelect
            ) {
                const handled = options.onNewAgentActionSelect();
                if (handled !== false) {
                    closeFlyout();
                    return;
                }
            }

            if (action.actionType === "navigate") {
                const appId = String(action.payload?.appId ?? activeDockButton?.id ?? "");
                const pageId = String(
                    action.payload?.pageId ?? activeDockButton?.homePage ?? CONFIG.FIXED_TAB_HOME,
                );
                const shouldForceReload =
                    action.id === "new-chat" ||
                    Boolean((action.payload as { forceReload?: boolean } | undefined)?.forceReload);
                const subPageToken = shouldForceReload
                    ? `refresh-${Date.now()}-${Math.random().toString(16).slice(2)}`
                    : null;

                if (appId) {
                    // Close flyout before navigating to avoid stale open state.
                    setEngagement(null);
                    closeFlyout();
                    navigateToPage(appId, pageId, subPageToken);
                }

                return;
            }

            if (optionId === "bulk-delete-chats") {
                dispatch({
                    type: "OPEN_CONFIRM",
                    confirm: { kind: "bulk-unpinned" },
                });
                return;
            }

            if (action.id === "chat-bulk-actions" && optionId) {
                if (optionId === "bulk-delete-chats") {
                    dispatch({
                        type: "OPEN_CONFIRM",
                        confirm: { kind: "bulk-unpinned" },
                    });
                    return;
                }
                if (optionId === "bulk-download-files") {
                    dispatch({ type: "SET_TOAST", toast: "Preparing files for download..." });

                    void downloadAllUploadedFiles()
                        .then(() => {
                            dispatch({ type: "SET_TOAST", toast: "Uploaded files download started." });
                        })
                        .catch((error) => {
                            console.error("[useDockFlyout] uploaded files download failed", error);
                            dispatch({
                                type: "SET_TOAST",
                                toast: "Download failed. Please try again.",
                            });
                        });
                    return;
                }
                if (optionId === "bulk-download-chats") {
                    dispatch({ type: "SET_TOAST", toast: "Preparing chats for download..." });

                    void downloadAllChats()
                        .then(() => {
                            dispatch({ type: "SET_TOAST", toast: "Chat download started." });
                        })
                        .catch((error) => {
                            console.error("[useDockFlyout] bulk chat download failed", error);
                            dispatch({
                                type: "SET_TOAST",
                                toast: "Download failed. Please try again.",
                            });
                        });
                    return;
                }
            }

            // TODO: Wire modal/custom header actions when backend handlers are connected.
            console.debug("Dock flyout header action", {
                actionId: action.id,
                optionId,
                actionType: action.actionType,
                payload: action.payload,
            });
        },
        [
            activeDockButton,
            closeFlyout,
            navigateToPage,
            options,
            setEngagement,
            state.activeDockButton?.kind,
        ],
    );

    const toggleSelection = useCallback((itemId: string) => {
        dispatch({ type: "TOGGLE_SELECTION", itemId });
    }, []);

    const clearSelection = useCallback(() => {
        dispatch({ type: "CLEAR_SELECTION" });
    }, []);

    const exportSelection = useCallback(() => {
        const selectedIds = state.feature.selectedIds;
        if (selectedIds.length === 0) return;

        dispatch({ type: "SET_TOAST", toast: "Preparing selected chats for download..." });
        void downloadSelectedChats(selectedIds)
            .then(() => {
                dispatch({ type: "CLEAR_SELECTION" });
                dispatch({ type: "SET_TOAST", toast: "Chat download started." });
            })
            .catch((error) => {
                console.error("[useDockFlyout] selected chat export failed", error);
                dispatch({ type: "SET_TOAST", toast: "Download failed. Please try again." });
            });
    }, [state.feature.selectedIds]);

    const extendSelectionTo = useCallback(
        (targetId: string) => {
            const orderedIds = listItems
                .filter((item) => !item.isAggregate)
                .map((item) => item.id);
            const anchor = state.feature.lastClickedId ?? state.feature.selectedIds[0] ?? targetId;
            const startIdx = orderedIds.indexOf(anchor);
            const endIdx = orderedIds.indexOf(targetId);

            if (startIdx === -1 || endIdx === -1) {
                dispatch({
                    type: "SET_SELECTION",
                    ids: [targetId],
                    anchorId: targetId,
                });
                return;
            }

            const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
            const rangeIds = orderedIds.slice(lo, hi + 1);
            dispatch({
                type: "SET_SELECTION",
                ids: rangeIds,
                anchorId: anchor,
            });
        },
        [listItems, state.feature.lastClickedId, state.feature.selectedIds],
    );

    const openConfirm = useCallback((confirm: DockConfirmState) => {
        dispatch({ type: "OPEN_CONFIRM", confirm });
    }, []);

    const closeConfirm = useCallback(() => {
        dispatch({ type: "CLOSE_CONFIRM" });
    }, []);

    const confirmDelete = useCallback(() => {
        const confirm = state.feature.confirm;
        if (!confirm) return;

        let ids: string[] = [];
        if (confirm.kind === "single" && confirm.targetId) {
            ids = [confirm.targetId];
        } else if (confirm.kind === "multi-delete") {
            ids = state.feature.selectedIds;
        } else if (confirm.kind === "bulk-unpinned") {
            const pinnedSet = new Set(state.feature.pinnedIds);
            ids = listItems
                .filter(
                    (item) =>
                        !item.isAggregate && !item.pinned && !pinnedSet.has(item.id),
                )
                .map((item) => item.id);
        }

        if (ids.length === 0) {
            dispatch({ type: "CLOSE_CONFIRM" });
            return;
        }

        // Optimistically hide the rows so the UI feels instant, then call the
        // backend. On failure we surface a toast; a full undo would require
        // re-fetching the list which is out of scope for this pass.
        dispatch({ type: "DELETE_ITEMS", ids });

        if (flyoutKind === "chats") {
            const mutation =
                confirm.kind === "bulk-unpinned"
                    ? deleteAllChatSessions().then((res) => ({
                        ok: res.Status,
                        failed: res.Status ? [] : ids,
                        deleted: res.DeletedSessionIds?.length
                            ? res.DeletedSessionIds
                            : res.Status ? ids : [],
                    }))
                    : ids.length === 1
                        ? deleteChatSession(ids[0]).then((ok) => ({
                            ok,
                            failed: ok ? [] : ids,
                            deleted: ok ? ids : [],
                        }))
                        : bulkDeleteChatSessions(ids).then((res) => ({
                            ok: res.Status,
                            failed: res.Results.filter((r) => !r.IsDeleteSuccessful).map(
                                (r) => r.SessionId,
                            ),
                            deleted: res.Results.filter((r) => r.IsDeleteSuccessful).map(
                                (r) => r.SessionId,
                            ),
                        }));

            mutation
                .then(({ ok, failed, deleted }) => {
                    if (deleted.length > 0) {
                        window.dispatchEvent(
                            new CustomEvent("kci-chat-sessions-deleted", {
                                detail: { sessionIds: deleted },
                            }),
                        );
                    }

                    if (!ok || failed.length > 0) {
                        dispatch({
                            type: "SET_TOAST",
                            toast: `Failed to delete ${failed.length || ids.length} chat${(failed.length || ids.length) === 1 ? "" : "s"
                                }.`,
                        });
                    }
                })
                .catch((error) => {
                    console.error("[useDockFlyout] chat delete failed", error);
                    dispatch({
                        type: "SET_TOAST",
                        toast: "Failed to delete chats. Please try again.",
                    });
                });
        }
    }, [
        state.feature.confirm,
        state.feature.selectedIds,
        state.feature.pinnedIds,
        listItems,
        flyoutKind,
    ]);

    const dismissToast = useCallback(() => {
        dispatch({ type: "SET_TOAST", toast: null });
    }, []);

    return {
        activeDockButton,
        anchorRect: state.anchorRect,
        secondaryAnchorRect: state.secondaryAnchorRect,
        isOpen,
        flyoutKind,
        flyoutConfig: effectiveFlyoutConfig,
        flyoutTitle,
        getGroupedRenderHints,

        registerTriggerClick,
        openFlyout,
        toggleFlyout,
        closeFlyout,

        query: state.feature.query,
        // Thin action wrappers exposed to UI to keep reducer private.
        setQuery: useCallback((value: string) => {
            dispatch(createSetQueryAction(value));
        }, []),
        clearQuery: useCallback(() => {
            dispatch(createSetQueryAction(""));
        }, []),

        selectedScope: state.feature.selectedScope,
        setSelectedScope: useCallback((value: string) => {
            dispatch(createSetScopeAction(value));
        }, []),

        data: state.feature.data,
        displayData,
        isLoading: state.feature.isLoading,
        isLoadingMore: state.feature.isLoadingMore,
        hasMore: state.feature.data?.pagination.hasMore ?? false,
        loadNextPage,
        listItems,
        activeDropdownLabel,
        dropdownOptions,

        filterSections: state.feature.data?.filterSections ?? [],
        appliedFilters: state.feature.appliedFilters,
        draftFilters: state.feature.draftFilters,

        isFilterPanelOpen: state.feature.isFilterPanelOpen,
        // Filter panel control callbacks.
        openFilterPanel: useCallback(() => {
            dispatch(createOpenFilterPanelAction());
        }, []),
        closeFilterPanel: useCallback(() => {
            dispatch(createCloseFilterPanelAction());
        }, []),
        applyDraftFilters: useCallback(() => {
            dispatch(createApplyDraftFiltersAction());
        }, []),
        resetDraftFilters: useCallback(() => {
            dispatch(createResetDraftFiltersAction());
        }, []),
        setFilterValue: useCallback((sectionId: string, value: string) => {
            dispatch(createSetFilterValueAction(sectionId, value));
        }, []),
        toggleFilterValue: useCallback((sectionId: string, value: string) => {
            dispatch(createToggleFilterValueAction(sectionId, value));
        }, []),
        toggleAllFilterValues: useCallback((sectionId: string) => {
            dispatch(createToggleAllFilterValuesAction(sectionId));
        }, []),
        areAllFilterValuesSelected,

        openActionMenuId: state.feature.openActionMenuId,
        // Row action menu callbacks.
        toggleRowMenu: useCallback((itemId: string) => {
            dispatch(createToggleRowMenuAction(itemId));
        }, []),
        closeRowMenu: useCallback(() => {
            dispatch(createCloseRowMenuAction());
        }, []),
        navigateToItem,
        togglePin,
        executeRowAction,
        pinnedIds: state.feature.pinnedIds,

        activeSecondaryItem,
        // Nested secondary panel callbacks.
        openSecondaryForRow: useCallback((itemId: string, anchorRect: DOMRect | null) => {
            if (flyoutKind !== "workspaces") {
                dispatch(createOpenSecondaryRowAction(itemId, anchorRect));
                return;
            }

            latestSecondaryRequestedItemIdRef.current = itemId;

            const row = state.feature.data?.items.find((item) => item.id === itemId);
            if (!row) {
                dispatch(createCloseSecondaryRowAction());
                return;
            }

            if (Array.isArray(row.secondaryGroups)) {
                dispatch({
                    type: "HYDRATE_SECONDARY_GROUPS",
                    itemId,
                    groups: row.secondaryGroups,
                });
                dispatch(createOpenSecondaryRowAction(itemId, anchorRect));
                return;
            }

            if (secondaryLoadInFlightRef.current.has(itemId)) {
                return;
            }

            // Keep the secondary panel hidden until this row's data arrives.
            dispatch(createCloseSecondaryRowAction());

            const workspaceIdFromFilter =
                typeof row.filters?.workspaceId === "string"
                    ? row.filters.workspaceId.trim()
                    : "";
            const workspaceId = workspaceIdFromFilter || row.id;
            if (!workspaceId) {
                dispatch(createCloseSecondaryRowAction());
                return;
            }

            const requestToken = ++secondaryRequestTokenRef.current;
            secondaryLoadInFlightRef.current.add(itemId);
            void fetchWorkspaceObjectives(workspaceId, row)
                .then((response) => {
                    const isStaleRequest =
                        requestToken !== secondaryRequestTokenRef.current ||
                        latestSecondaryRequestedItemIdRef.current !== itemId;

                    if (isStaleRequest) {
                        return;
                    }

                    const objectiveGroup = buildObjectiveSecondaryGroup(
                        workspaceId,
                        response.objectives,
                    );

                    const groups = objectiveGroup.items.length > 0 ? [objectiveGroup] : [];
                    dispatch({
                        type: "HYDRATE_SECONDARY_GROUPS",
                        itemId,
                        groups,
                    });
                    dispatch(createOpenSecondaryRowAction(itemId, anchorRect));
                })
                .catch((error) => {
                    const isStaleRequest =
                        requestToken !== secondaryRequestTokenRef.current ||
                        latestSecondaryRequestedItemIdRef.current !== itemId;

                    if (isStaleRequest) {
                        return;
                    }

                    dispatch(createCloseSecondaryRowAction());
                    console.warn(
                        "[useDockFlyout] Failed to fetch workspace objectives",
                        { itemId, workspaceId, error },
                    );
                })
                .finally(() => {
                    secondaryLoadInFlightRef.current.delete(itemId);
                });
        }, [flyoutKind, state.feature.data]),
        closeSecondary: useCallback(() => {
            latestSecondaryRequestedItemIdRef.current = null;
            secondaryRequestTokenRef.current += 1;
            dispatch(createCloseSecondaryRowAction());
        }, []),
        openSecondaryGroupId: state.feature.openSecondaryGroupId,
        setOpenSecondaryGroupId: useCallback((groupId: string | null) => {
            dispatch(createSetOpenSecondaryGroupAction(groupId));
        }, []),
        syncWorkspaceObjectives: useCallback((): Promise<void> => {
            if (flyoutKind !== "workspaces" || !activeSecondaryItem) {
            return Promise.resolve();
            }

            const workspaceIdFromFilter =
                typeof activeSecondaryItem.filters?.workspaceId === "string"
                    ? activeSecondaryItem.filters.workspaceId.trim()
                    : "";
            const workspaceId = workspaceIdFromFilter || activeSecondaryItem.id;
            if (!workspaceId || workspaceSyncInFlightRef.current.has(workspaceId)) {
                return Promise.resolve();
            }

            const itemId = activeSecondaryItem.id;
            const existingGroups = activeSecondaryItem.secondaryGroups ?? [];
            workspaceSyncInFlightRef.current.add(workspaceId);

            return syncWorkspace(workspaceId, activeSecondaryItem)
                .then((response) => {
                    const createdGroup = buildSyncedObjectiveSecondaryGroup(
                        workspaceId,
                        response.createdObjectives,
                    );
                    if (createdGroup.items.length === 0) {
                        return;
                    }

                    const targetGroupId = existingGroups[0]?.id ?? createdGroup.id;
                    const existingIds = new Set(
                        existingGroups.flatMap((group) => group.items.map((item) => item.id)),
                    );
                    const newItems = createdGroup.items.filter((item) => !existingIds.has(item.id));
                    if (newItems.length === 0) {
                        return;
                    }

                    const groups = existingGroups.length > 0
                        ? existingGroups.map((group) =>
                            group.id === targetGroupId
                                ? { ...group, items: [...group.items, ...newItems] }
                                : group,
                        )
                        : [{ ...createdGroup, items: newItems }];

                    dispatch({
                        type: "HYDRATE_SECONDARY_GROUPS",
                        itemId,
                        groups,
                    });
                })
                .catch((error) => {
                    console.warn("[useDockFlyout] Failed to sync workspace objectives", {
                        itemId,
                        workspaceId,
                        error,
                    });
                })
                .finally(() => {
                    workspaceSyncInFlightRef.current.delete(workspaceId);
                });
        }, [activeSecondaryItem, flyoutKind]),

        executeHeaderAction,

        selectedIds: state.feature.selectedIds,
        toggleSelection,
        extendSelectionTo,
        clearSelection,
        exportSelection,

        confirm: state.feature.confirm,
        openConfirm,
        closeConfirm,
        confirmDelete,

        renameDialog,
        setRenameDraftName,
        closeRenameDialog,
        submitRename,
        isRenaming,

        toast: state.feature.toast,
        dismissToast,

        // CRUD Dialog Management
        crudDialog: state.feature.crudDialog,
        showRenameDialog: useCallback((itemId: string, itemType: DockCrudItemType, currentTitle: string) => {
            dispatch({ type: "SHOW_RENAME_DIALOG", itemId, itemType, currentTitle });
        }, []),
        showDeleteDialog: useCallback((itemId: string, itemType: DockCrudItemType, itemName: string) => {
            dispatch({ type: "SHOW_DELETE_DIALOG", itemId, itemType, itemName });
        }, []),
        showShareDialog: useCallback((itemId: string, itemType: DockCrudItemType) => {
            dispatch({ type: "SHOW_SHARE_DIALOG", itemId, itemType });
        }, []),
        showEditDialog: useCallback((itemId: string, itemType: DockCrudItemType, agentData?: unknown) => {
            dispatch({ type: "SHOW_EDIT_DIALOG", itemId, itemType, agentData });
        }, []),
        showDownloadDialog: useCallback((itemId: string, itemType: DockCrudItemType) => {
            dispatch({ type: "SHOW_DOWNLOAD_DIALOG", itemId, itemType });
        }, []),
        hideCrudDialog: useCallback(() => {
            dispatch({ type: "HIDE_CRUD_DIALOG" });
        }, []),
        notifyItemRenamed: useCallback((itemId: string, label: string) => {
            dispatch({ type: "UPDATE_ITEM_LABEL", itemId, label });
        }, []),
        notifyItemDeleted: useCallback((itemId: string) => {
            dispatch({ type: "REMOVE_ITEM", itemId });
        }, []),
        refreshData: useCallback(() => {
            if (!isOpen || !flyoutKind) return;
            void requestDockFlyoutPage(
                false,
                buildDockLoadParams(1, pageSize, debouncedQuery, state.feature.selectedScope, stableAppliedFilters),
            );
        }, [isOpen, flyoutKind, requestDockFlyoutPage, pageSize, debouncedQuery, state.feature.selectedScope, stableAppliedFilters]),
    };
}

export default useDockFlyout;